import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { AccountDeletionSummary, DeleteAccountResult, Me } from "../types";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "../contact";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, Field, PageHeader, Toggle } from "../components/ui";

export function Settings() {
  const me = useOutletContext<Me>();
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [memberEmail, setMemberEmail] = useState("");

  const health = useQuery({
    queryKey: ["inbox-health"],
    queryFn: () => api.get<{ ok: boolean; detail: string }>("/inbox/health"),
    enabled: me.isAdmin,
  });

  const addSender = useMutation({
    mutationFn: () => api.post<{ senders: string[] }>("/senders", { email }),
    onSuccess: () => {
      setEmail("");
      toast.success("Forwarder added");
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const removeSender = useMutation({
    mutationFn: (e: string) =>
      api.del<{ senders: string[] }>(`/senders/${encodeURIComponent(e)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
    onError: (e) => toast.error((e as Error).message),
  });

  const addMember = useMutation({
    mutationFn: () => api.post("/household/members", { email: memberEmail }),
    onSuccess: () => {
      setMemberEmail("");
      toast.success("Invite sent");
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const removeMember = useMutation({
    mutationFn: (e: string) =>
      api.del(`/household/members/${encodeURIComponent(e)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
    onError: (e) => toast.error((e as Error).message),
  });

  // Each control saves just its own field; the server keeps the rest.
  const saveNotify = useMutation({
    mutationFn: (
      v: Partial<
        Pick<
          Me,
          | "notifyEmail"
          | "notifyNewForm"
          | "notifyDaysBefore"
          | "notifyDueDay"
          | "notifyOrderSynced"
          | "notifyOrderProblems"
        >
      >,
    ) => api.patch("/me/notifications", v),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const copyInbox = async () => {
    try {
      await navigator.clipboard.writeText(me.appInbox);
      toast.success("Inbox address copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <>
      <PageHeader title="Settings" />

      <Card
        title="App inbox"
        sub="Forward Google Forms lunch receipts here from an authorized address below."
      >
        <div className="cluster">
          <code className="mono">{me.appInbox}</code>
          <Button variant="ghost" size="sm" icon="copy" onClick={copyInbox}>
            Copy
          </Button>
        </div>
        {me.isAdmin && (
          <p style={{ marginTop: 12 }}>
            {health.isLoading ? (
              <span className="muted small">checking…</span>
            ) : health.data?.ok ? (
              <Badge tone="synced" dot>
                reachable
              </Badge>
            ) : (
              <Badge tone="error" dot>
                unreachable
              </Badge>
            )}{" "}
            <span className="muted small">{health.data?.detail}</span>
          </p>
        )}
      </Card>

      <Card
        title="Authorized forwarders"
        sub="Emails forwarded from any of these addresses link to your account. An address can belong to only one account."
      >
        <div className="cluster" style={{ marginBottom: 14 }}>
          {me.senders.map((s) => (
            <span key={s} className="chip">
              <Icon name="mail" size={13} className="faint" />
              {s}
              {s !== me.user.email.toLowerCase() && (
                <button
                  className="x"
                  onClick={() => removeSender.mutate(s)}
                  aria-label={`Remove ${s}`}
                >
                  <Icon name="x" size={14} />
                </button>
              )}
            </span>
          ))}
        </div>
        <div className="row" style={{ alignItems: "flex-end" }}>
          <Field label="Add an address">
            <input
              type="email"
              placeholder="spouse@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && email && addSender.mutate()
              }
            />
          </Field>
          <div style={{ flex: "0 0 auto" }}>
            <Button
              icon="plus"
              disabled={!email}
              loading={addSender.isPending}
              onClick={() => addSender.mutate()}
            >
              Add
            </Button>
          </div>
        </div>
      </Card>

      <Card
        title="Household"
        sub="Invite another parent to sign in with their own Google account. Your children show up on their Dashboard, Children, and Order pages alongside their own. They can order and view, but can't change your children, calendars, or forwarders."
      >
        {(me.members?.length ?? 0) > 0 && (
          <div className="cluster" style={{ marginBottom: 14 }}>
            {me.members.map((m) => (
              <span key={m.member_email} className="chip">
                <Icon name="user" size={13} className="faint" />
                {m.member_email}
                <Badge tone={m.member_user_id ? "synced" : "pending"}>
                  {m.member_user_id ? "active" : "pending"}
                </Badge>
                <button
                  className="x"
                  onClick={() => removeMember.mutate(m.member_email)}
                  aria-label={`Remove ${m.member_email}`}
                >
                  <Icon name="x" size={14} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="row" style={{ alignItems: "flex-end" }}>
          <Field label="Invite by email">
            <input
              type="email"
              placeholder="spouse@gmail.com"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && memberEmail && addMember.mutate()
              }
            />
          </Field>
          <div style={{ flex: "0 0 auto" }}>
            <Button
              icon="plus"
              disabled={!memberEmail}
              loading={addMember.isPending}
              onClick={() => addMember.mutate()}
            >
              Invite
            </Button>
          </div>
        </div>
        {(me.households?.length ?? 0) > 0 && (
          <p className="muted small" style={{ marginTop: 14 }}>
            You're in the household of{" "}
            {me.households.map((h) => h.ownerName || h.ownerEmail).join(", ")}
            , so their children appear alongside yours.
          </p>
        )}
      </Card>

      <Card
        title="Email notifications"
        sub={`Sent to ${me.user.email}.${
          me.emailEnabled ? "" : " Email isn't set up on the server yet, so these won't send."
        }`}
      >
        <Toggle
          checked={me.notifyEmail}
          onChange={(v) => saveNotify.mutate({ notifyEmail: v })}
          label={<b>Send me email</b>}
        />
        <p className="muted small" style={{ margin: "4px 0 0" }}>
          {me.notifyEmail
            ? "Choose which emails you get below."
            : "All email from Caldwell School Lunch is off. You'll still see everything in the app."}
        </p>

        <fieldset className="notify-groups" disabled={!me.notifyEmail}>
          <div className="notify-group">
            <h4>Order forms</h4>
            <Toggle
              checked={me.notifyNewForm}
              onChange={(v) => saveNotify.mutate({ notifyNewForm: v })}
              label="When a new month's order form is posted"
            />
            <div className="row" style={{ marginTop: 4, maxWidth: 360 }}>
              <Field
                label="Before the due date"
                hint="If a child's order isn't in yet."
              >
                <select
                  value={me.notifyDaysBefore}
                  onChange={(e) =>
                    saveNotify.mutate({ notifyDaysBefore: Number(e.target.value) })
                  }
                >
                  <option value={0}>No reminder</option>
                  <option value={1}>1 day before</option>
                  <option value={2}>2 days before</option>
                  <option value={3}>3 days before</option>
                  <option value={5}>5 days before</option>
                  <option value={7}>1 week before</option>
                </select>
              </Field>
            </div>
            <Toggle
              checked={me.notifyDueDay}
              onChange={(v) => saveNotify.mutate({ notifyDueDay: v })}
              label="On the morning it's due, if a child's order still isn't in"
            />
            <p className="muted small" style={{ margin: 0 }}>
              Reminders skip any child whose order confirmation has already
              been forwarded.
            </p>
          </div>

          <div className="notify-group">
            <h4>Forwarded orders</h4>
            <Toggle
              checked={me.notifyOrderSynced}
              onChange={(v) => saveNotify.mutate({ notifyOrderSynced: v })}
              label="When a forwarded order updates the calendar"
            />
            <Toggle
              checked={me.notifyOrderProblems}
              onChange={(v) => saveNotify.mutate({ notifyOrderProblems: v })}
              label="When a forwarded order needs a look (the child isn't set up, or it doesn't match your picks)"
            />
          </div>
        </fieldset>
      </Card>

      <Card
        title="Google account"
        sub={`Calendar access is granted for ${me.user.email}.`}
      >
        <a className="btn secondary" href="/auth/google?reconnect=1">
          <Icon name="refresh" size={16} />
          Reconnect Google
        </a>
        <p className="muted small" style={{ marginTop: 10 }}>
          Normal sign-in reuses your existing permission. Use this only if
          calendar sync stops working.
        </p>
      </Card>

      <Card title="Need help?">
        <p className="muted small" style={{ margin: 0 }}>
          If you need help, you can contact us at{" "}
          <a href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</a>.
        </p>
      </Card>

      <DeleteAccountCard />
    </>
  );
}

function DeleteAccountCard() {
  const [open, setOpen] = useState(false);
  const [removeEvents, setRemoveEvents] = useState(true);
  const [confirm, setConfirm] = useState("");
  const toast = useToast();

  const summary = useQuery({
    queryKey: ["deletion-summary"],
    queryFn: () => api.get<AccountDeletionSummary>("/me/deletion-summary"),
    enabled: open,
  });

  const del = useMutation({
    mutationFn: () =>
      api.del<DeleteAccountResult>("/me", { removeEvents, confirm: "DELETE" }),
    // The session is gone with the account, so leave the SPA entirely rather
    // than letting react-query refetch into a 401 loop.
    onSuccess: () => window.location.replace("/about"),
    onError: (e) => toast.error((e as Error).message),
  });

  const s = summary.data;
  const close = () => {
    setOpen(false);
    setConfirm("");
  };

  return (
    <>
      <Card
        title="Delete account"
        sub="Remove this account and everything in it."
      >
        <p className="muted small" style={{ marginBottom: 12 }}>
          Your children, orders, drafts and household links are deleted. This
          cannot be undone.
        </p>
        <Button variant="danger" onClick={() => setOpen(true)}>
          <Icon name="trash" size={16} />
          Delete account
        </Button>
      </Card>

      {open && (
        <Modal
          title="Delete your account?"
          onClose={close}
          footer={
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={del.isPending}
                disabled={confirm !== "DELETE" || summary.isPending}
                onClick={() => del.mutate()}
              >
                Delete account
              </Button>
            </>
          }
        >
          {summary.isPending ? (
            <p className="muted">Checking what this will remove...</p>
          ) : (
            <>
              <p style={{ marginBottom: 12 }}>
                This removes{" "}
                <b>
                  {s?.children ?? 0} {s?.children === 1 ? "child" : "children"}
                </b>
                , <b>{s?.submissions ?? 0} orders</b> and every draft and
                household link on this account. It cannot be undone.
              </p>

              {!!s?.calendarEvents && (
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={removeEvents}
                    onChange={(e) => setRemoveEvents(e.target.checked)}
                  />
                  <span>
                    Also remove the <b>{s.calendarEvents}</b> lunch{" "}
                    {s.calendarEvents === 1 ? "event" : "events"} this app
                    created from my calendars
                  </span>
                </label>
              )}

              {!!s?.sharedWith.length && (
                <div className="callout warn" style={{ marginTop: 12 }}>
                  <Icon name="alert-circle" size={18} />
                  <span className="small">
                    {s.sharedWith.join(", ")} will lose access to your children.
                  </span>
                </div>
              )}

              <Field
                label="Type DELETE to confirm"
                hint="This is your last chance to back out."
              >
                <input
                  type="text"
                  autoComplete="off"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </Field>
            </>
          )}
        </Modal>
      )}
    </>
  );
}
